import React from 'react';
import { CheckCircle as CheckCircleIcon, Warning as WarningIcon, Info as InfoIcon } from '@mui/icons-material';
import { getSimilarityConfidenceMessage, groupByConfidenceLevel } from '../utils/knowledgeBaseHelpers';
import { Button } from './ui/Button';

interface SimilarQuestion {
  _id: string;
  question: string;
  answer: string;
  category: string;
  type: 'objection' | 'faq';
  isOriginal: boolean;
  parentId?: string;
  parentQuestion?: string;
  variations: Array<{ _id: string; question: string }>;
  similarity: number;
  similarityDetails: {
    levenshtein: number;
    ngram: number;
    token: number;
    wordOrder: number;
  };
  matchType: string;
  confidenceLevel: 'high' | 'medium' | 'low';
}

interface SimilarityCheckStepProps {
  similarQuestions: SimilarQuestion[];
  newQuestion: string;
  editedQuestion?: string;
  onQuestionChange?: (question: string) => void;
  onUseExisting: (question: SimilarQuestion) => void;
  onAddAsVariation: (parentQuestion: SimilarQuestion) => void;
  onAddAsNew: () => void;
  onCancel: () => void;
  isProcessing: boolean;
}

const SimilarityCheckStep: React.FC<SimilarityCheckStepProps> = ({
  similarQuestions,
  newQuestion,
  editedQuestion,
  onQuestionChange,
  onUseExisting,
  onAddAsVariation,
  onAddAsNew,
  onCancel,
  isProcessing
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [localEditedQuestion, setLocalEditedQuestion] = React.useState(editedQuestion || newQuestion);
  const getConfidenceIcon = (level: string) => {
    switch (level) {
      case 'high':
        return <WarningIcon className="text-error" style={{ fontSize: '1.25rem' }} />;
      case 'medium':
        return <InfoIcon className="text-warning" style={{ fontSize: '1.25rem' }} />;
      case 'low':
        return <CheckCircleIcon className="text-info" style={{ fontSize: '1.25rem' }} />;
      default:
        return null;
    }
  };

  const getConfidenceMessage = getSimilarityConfidenceMessage;

  const getSimilarityBadgeColor = (level: string) => {
    switch (level) {
      case 'high':
        return 'bg-error-tint text-error border-error';
      case 'medium':
        return 'bg-warning-tint text-warning border-warning';
      case 'low':
        return 'bg-info-tint text-info border-info';
      default:
        return 'bg-surface-subtle text-ink border-hairline';
    }
  };

  // Group questions by confidence level
  const {
    high: highConfidence,
    medium: mediumConfidence,
    low: lowConfidence
  } = groupByConfidenceLevel(similarQuestions);

  return (
    <div className="space-y-4">
      {/* Header Message */}
      <div className="bg-info-tint border border-info rounded-lg p-4">
        <div className="flex items-start gap-3">
          <InfoIcon className="text-info mt-0.5" style={{ fontSize: '1.25rem' }} />
          <div>
            <p className="text-sm font-medium text-info">
              Similar questions found in the knowledge base
            </p>
            <p className="text-sm text-info mt-1">
              We found {similarQuestions.length} similar question{similarQuestions.length !== 1 ? 's' : ''}.
              You can either use an existing one or add your question as new.
            </p>
          </div>
        </div>
      </div>

      {/* New Question Display/Edit */}
      <div className="bg-surface-subtle rounded-lg p-3">
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs font-medium text-ink-muted">Your new question:</p>
          {!isEditing && (
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => setIsEditing(true)}
              className="!p-0 !text-xs text-signature hover:text-signature-strong"
            >
              Edit
            </Button>
          )}
        </div>
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              value={localEditedQuestion}
              onChange={(e) => {
                setLocalEditedQuestion(e.target.value);
                if (onQuestionChange) {
                  onQuestionChange(e.target.value);
                }
              }}
              className="w-full px-2 py-1 text-sm border border-ink-border rounded-md focus:outline-none focus:ring-2 focus:ring-signature"
              rows={3}
            />
            <div className="flex gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setIsEditing(false);
                  if (onQuestionChange && localEditedQuestion !== (editedQuestion || newQuestion)) {
                    onQuestionChange(localEditedQuestion);
                  }
                }}
              >
                Save
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setLocalEditedQuestion(editedQuestion || newQuestion);
                  setIsEditing(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-ink">{localEditedQuestion}</p>
        )}
      </div>

      {/* Similar Questions List */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {/* High Confidence Matches */}
        {highConfidence.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-error flex items-center gap-2">
              <WarningIcon className="text-error" style={{ fontSize: '1rem' }} />
              Likely Duplicates
            </h4>
            {highConfidence.map((question) => (
              <QuestionCard
                key={question._id}
                question={question}
                onUseExisting={onUseExisting}
                onAddAsVariation={onAddAsVariation}
                isProcessing={isProcessing}
                getConfidenceIcon={getConfidenceIcon}
                getConfidenceMessage={getConfidenceMessage}
                getSimilarityBadgeColor={getSimilarityBadgeColor}
              />
            ))}
          </div>
        )}

        {/* Medium Confidence Matches */}
        {mediumConfidence.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-warning flex items-center gap-2">
              <InfoIcon className="text-warning" style={{ fontSize: '1rem' }} />
              Possible Variations
            </h4>
            {mediumConfidence.map((question) => (
              <QuestionCard
                key={question._id}
                question={question}
                onUseExisting={onUseExisting}
                onAddAsVariation={onAddAsVariation}
                isProcessing={isProcessing}
                getConfidenceIcon={getConfidenceIcon}
                getConfidenceMessage={getConfidenceMessage}
                getSimilarityBadgeColor={getSimilarityBadgeColor}
              />
            ))}
          </div>
        )}

        {/* Low Confidence Matches */}
        {lowConfidence.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-info flex items-center gap-2">
              <CheckCircleIcon className="text-info" style={{ fontSize: '1rem' }} />
              Related Questions
            </h4>
            {lowConfidence.map((question) => (
              <QuestionCard
                key={question._id}
                question={question}
                onUseExisting={onUseExisting}
                onAddAsVariation={onAddAsVariation}
                isProcessing={isProcessing}
                getConfidenceIcon={getConfidenceIcon}
                getConfidenceMessage={getConfidenceMessage}
                getSimilarityBadgeColor={getSimilarityBadgeColor}
              />
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button variant="secondary" onClick={onCancel} disabled={isProcessing}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onAddAsNew} disabled={isProcessing}>
          Add as New Original Question
        </Button>
      </div>
    </div>
  );
};

// Separate component for each question card
const QuestionCard: React.FC<{
  question: SimilarQuestion;
  onUseExisting: (question: SimilarQuestion) => void;
  onAddAsVariation: (question: SimilarQuestion) => void;
  isProcessing: boolean;
  getConfidenceIcon: (level: string) => React.ReactNode;
  getConfidenceMessage: (level: string, similarity: number) => string;
  getSimilarityBadgeColor: (level: string) => string;
}> = ({
  question,
  onUseExisting,
  onAddAsVariation,
  isProcessing,
  getConfidenceIcon,
  getConfidenceMessage,
  getSimilarityBadgeColor,
}) => {
  return (
    <div className="border rounded-lg p-4 bg-white hover:shadow-sm transition-shadow">
      <div className="space-y-2">
        {/* Similarity Badge and Type */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getConfidenceIcon(question.confidenceLevel)}
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getSimilarityBadgeColor(question.confidenceLevel)}`}>
              {getConfidenceMessage(question.confidenceLevel, question.similarity)}
            </span>
          </div>
          <span className="text-xs text-ink-muted uppercase">{question.type}</span>
        </div>

        {/* Question Text */}
        <div>
          <p className="text-sm font-medium text-ink">{question.question}</p>
          {question.parentQuestion && (
            <p className="text-xs text-ink-muted mt-1">
              Variation of: "{question.parentQuestion}"
            </p>
          )}
        </div>

        {/* Answer Preview */}
        <div className="bg-surface-subtle rounded p-2">
          <p className="text-xs text-ink-secondary line-clamp-2">{question.answer}</p>
        </div>

        {/* Similarity Details */}
        <details className="text-xs text-ink-muted">
          <summary className="cursor-pointer hover:text-ink-secondary">Match details</summary>
          <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
            <div>Text similarity: {question.similarityDetails.levenshtein}%</div>
            <div>N-gram match: {question.similarityDetails.ngram}%</div>
            <div>Token match: {question.similarityDetails.token}%</div>
            <div>Word order: {question.similarityDetails.wordOrder}%</div>
          </div>
        </details>

        {/* Variations Count */}
        {question.isOriginal && question.variations && question.variations.length > 0 && (
          <p className="text-xs text-ink-muted">
            Has {question.variations.length} variation{question.variations.length !== 1 ? 's' : ''}
          </p>
        )}

        {/* Actions */}
        <div className="flex gap-2 mt-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onUseExisting(question)}
            disabled={isProcessing}
            className="flex-1"
          >
            Use This (Mark as Processed)
          </Button>
          {question.isOriginal && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onAddAsVariation(question)}
              disabled={isProcessing}
              className="flex-1"
            >
              Add as Variation
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimilarityCheckStep;